import { useEffect, useState } from "react";
import { Eye, Pencil, X, Unlock, Download, FileText, Paperclip, CheckCircle2, Trash2, Eraser } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formTypeLabel, materialStatusColor, materialStatusLabel } from "../lib/formTypes";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Label } from "../components/ui/label";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 border-b py-1 last:border-0">
    <span className="text-xs uppercase text-slate-400">{label}</span>
    <span className="text-right font-medium text-slate-700">{value ?? "—"}</span>
  </div>
);

function AttachmentItem({ att }) {
  const [url, setUrl] = useState(null);
  const isImg = (att.final_mime || "").startsWith("image/");
  useEffect(() => {
    let obj;
    if (isImg) {
      api.get(`/data-entry/attachments/${att.id}/download`, { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [att.id, isImg]);
  const download = async () => {
    try {
      const r = await api.get(`/data-entry/attachments/${att.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = att.original_filename || "attachment"; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
  };
  const kb = att.final_size ? `${Math.round(att.final_size / 1024)} KB` : "";
  return (
    <div className="flex items-center gap-3 rounded-lg border p-2" data-testid={`de-att-${att.id}`}>
      {isImg && url
        ? <img src={url} alt={att.original_filename} className="h-12 w-12 rounded object-cover" />
        : <div className="flex h-12 w-12 items-center justify-center rounded bg-slate-100"><FileText className="h-5 w-5 text-slate-400" /></div>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-700">{att.original_filename}</div>
        <div className="text-xs text-slate-400">{(att.final_mime || "").split("/").pop()?.toUpperCase()} {kb && `· ${kb}`}</div>
      </div>
      <Button variant="ghost" size="sm" onClick={download} data-testid={`de-att-dl-${att.id}`}><Download className="h-4 w-4" /></Button>
    </div>
  );
}

export default function DataEntryRegister() {
  const { has } = useAuth();
  const { ctx, resumeDraft, drill, clearDrill } = useWorkspace();
  const pid = ctx.project?.id;
  const canComm = has("DATA_ENTRY.VIEW_COMMERCIALS");
  const canEdit = has("DATA_ENTRY.EDIT");
  const canDelete = has("DATA_ENTRY.DELETE");
  const canReopen = has("DATA_ENTRY.EDIT_COMPLETED");
  const canComplete = has("DATA_ENTRY.COMPLETE");
  const [refreshTick, setRefreshTick] = useState(0);
  const [completing, setCompleting] = useState(false);
  const completeFromDraft = async (id) => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/data-entry/${id}/complete-from-draft`);
      toast.success(`Completed · ${data.entry_number} · ${data.materials_consumed} material(s) consumed`);
      setDetail(null); setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Completion failed (HTTP ${e.response?.status || "network error / timeout"})`);
      if (e.response?.status === 409) { setDetail(null); setRefreshTick((t) => t + 1); }
    }
    finally { setCompleting(false); }
  };
  const reopen = async (id) => {
    if (!window.confirm("Reopen this completed entry for editing? Its materials will be freed for change.")) return;
    try { await api.post(`/data-entry/${id}/reopen`); resumeDraft(id); }
    catch (e) { toast.error(e.response?.data?.detail || "Reopen failed"); }
  };
  const delDraft = async (id) => {
    setDelTarget(id); setDelStep(1);
  };
  const confirmDelete = async () => {
    const id = delTarget;
    setDeleting(true);
    try {
      await api.delete(`/data-entry/${id}`);
      toast.success("Draft deleted");
      setDetail(null); setRefreshTick((t) => t + 1);
      setDelTarget(null); setDelStep(1);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };
  const [delTarget, setDelTarget] = useState(null);
  const [delStep, setDelStep] = useState(1);
  const [deleting, setDeleting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [drillFilters, setDrillFilters] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkResult, setBulkResult] = useState(null);

  const completeReady = async () => {
    if (!window.confirm("Complete ALL ready draft entries (every material Acknowledged/Installed) in one go? This cannot be undone.")) return;
    setBulkBusy(true);
    try {
      const { data: start } = await api.post("/data-entry/complete-ready", null, { params: pid ? { project_id: pid } : {} });
      if (start.already_running) toast.info("A bulk completion is already running for this project — showing its progress");
      let data;
      for (;;) {
        await new Promise((r) => setTimeout(r, 2500));
        ({ data } = await api.get(`/data-entry/complete-ready/${start.job_id}`));
        if (data.status !== "RUNNING") break;
        setBulkProgress(data.ready != null ? `${data.done || 0}/${data.ready}` : "…");
      }
      if (data.status === "FAILED") toast.error(`Bulk completion stopped: ${data.error}`);
      else if (data.failed?.length) setBulkResult({ ...data, job_id: start.job_id });
      else toast.success(`Completed ${data.completed.length} of ${data.ready} ready draft(s)` + (data.skipped_drafts ? ` · ${data.skipped_drafts} not ready yet` : ""));
      setRefreshTick((t) => t + 1);
    } catch (e) { toast.error(e.response?.data?.detail || `Bulk completion failed (HTTP ${e.response?.status || "network error / timeout"})`); }
    finally { setBulkBusy(false); setBulkProgress(""); }
  };

  const downloadFailedPdf = async () => {
    try {
      const r = await api.get(`/data-entry/complete-ready/${bulkResult.job_id}/failed-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Bulk_Complete_Failed_Entries.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(`PDF download failed (HTTP ${e.response?.status || "network error"})`); }
  };

  const [cleanBusy, setCleanBusy] = useState(false);
  const cleanBlankDrafts = async () => {
    setCleanBusy(true);
    try {
      const payload = { dry_run: true, ...(pid ? { project_id: pid } : {}) };
      const { data: pre } = await api.post("/data-entry/cleanup-blank-drafts", payload);
      if (!pre.blank_drafts) { toast.info("No blank drafts found — register is clean."); return; }
      const sample = (pre.sample || []).slice(0, 5).map((s) => s.entry_number).join(", ");
      if (!window.confirm(`Found ${pre.blank_drafts} blank draft(s) with no data (e.g. ${sample}${pre.blank_drafts > 5 ? ", …" : ""}).\n\nThese are leftovers from interrupted uploads — no data will be lost and no materials are consumed by them. Delete them?`)) return;
      const { data } = await api.post("/data-entry/cleanup-blank-drafts", { ...payload, dry_run: false });
      toast.success(`Removed ${data.deleted} blank draft(s)` + (data.skipped ? ` · ${data.skipped} skipped (in use)` : ""));
      setRefreshTick((t) => t + 1);
    } catch (e) { toast.error(e.response?.data?.detail || `Cleanup failed (HTTP ${e.response?.status || "network error"})`); }
    finally { setCleanBusy(false); }
  };
  const [frOpts, setFrOpts] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/data-entry/stats", { params: pid ? { project_id: pid } : {} })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, [pid, refreshTick]);

  // Date-wise summary tab (default: last 30 days)
  const _iso = (d) => d.toISOString().slice(0, 10);
  const [sumSingle, setSumSingle] = useState(false);
  const [sumDay, setSumDay] = useState(() => _iso(new Date()));
  const [sumFrom, setSumFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return _iso(d); });
  const [sumTo, setSumTo] = useState(() => _iso(new Date()));
  const [sumRows, setSumRows] = useState(null);

  useEffect(() => {
    const params = { ...(pid ? { project_id: pid } : {}) };
    if (sumSingle) { params.date_from = sumDay; params.date_to = sumDay; }
    else { if (sumFrom) params.date_from = sumFrom; if (sumTo) params.date_to = sumTo; }
    api.get("/data-entry/date-summary", { params }).then((r) => setSumRows(r.data.items || [])).catch(() => setSumRows([]));
  }, [pid, sumSingle, sumDay, sumFrom, sumTo]);

  useEffect(() => {
    if (!pid) { setFrOpts([]); return; }
    api.get("/data-entry/final-remarks", { params: { project_id: pid } })
      .then((r) => setFrOpts((r.data.items || []).map((x) => ({ value: x.id, label: x.text }))))
      .catch(() => setFrOpts([]));
  }, [pid]);

  // Consume a drill-down payload coming from the Dashboard Commercial Work Summary.
  useEffect(() => {
    if (drill && drill.key === "data-entry-register") {
      const { key, label, ...rest } = drill;
      setDrillFilters({ ...rest, label });
      clearDrill();
    }
  }, [drill, clearDrill]);

  const load = (params) => api.get("/data-entry", {
    params: { ...params, ...(pid ? { project_id: pid } : {}), ...(drillFilters ? {
      status: drillFilters.status || params.status,
      install_from: drillFilters.install_from,
      install_to: drillFilters.install_to,
      final_remark_id: drillFilters.final_remark_id,
    } : {}) },
  }).then((r) => r.data);
  const openDetail = async (row) => { const { data } = await api.get(`/data-entry/${row.id}`); setDetail(data); };

  const columns = [
    { key: "entry_number", label: "Entry No.", mono: true },
    { key: "form_name", label: "Form" },
    { key: "form_type", label: "Type", render: (r) => <span className="text-xs">{formTypeLabel(r.form_type)}</span> },
    { key: "consumer_number", label: "Consumer", mono: true, render: (r) => r.consumer_number || "—" },
    { key: "material_count", label: "Materials" },
    { key: "status", label: "Status", render: (r) => (
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1">
          <Badge className={r.status === "COMPLETED" ? "bg-emerald-600" : "bg-amber-500"}>{r.status}</Badge>
          {r.status === "DRAFT" && r.can_complete && (
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`de-ready-${r.id}`}>
              <CheckCircle2 className="mr-1 h-3 w-3" /> Ready
            </Badge>
          )}
          {r.source === "BULK_UPLOAD" && (
            <Badge variant="outline" className="border-sky-300 bg-sky-50 text-[10px] text-sky-700" title="Created by Excel bulk upload — attachment not mandatory" data-testid={`de-bulk-tag-${r.id}`}>
              Bulk upload
            </Badge>
          )}
        </div>
        {r.status === "DRAFT" && !r.can_complete && (r.pending_materials?.length > 0) && (
          <span className="text-[10px] leading-tight text-amber-600" data-testid={`de-pending-${r.id}`}
            title={r.pending_materials.map((m) => `${m.serial || "?"}${m.type_label ? ` (${m.type_label})` : ""} — ${materialStatusLabel(m.status)}`).join("\n")}>
            Draft: awaiting {r.pending_materials.length} material{r.pending_materials.length > 1 ? "s" : ""} — {r.pending_materials.slice(0, 2).map((m) => `${m.serial || "?"} (${materialStatusLabel(m.status)})`).join(", ")}{r.pending_materials.length > 2 ? "…" : ""}
          </span>
        )}
      </div>
    ) },
    ...(canComm ? [{ key: "commercial_total", label: "Value (₹)", render: (r) => r.commercial_total != null
        ? <span data-testid={`de-value-${r.id}`}>₹{r.commercial_total}{r.status === "DRAFT" && r.commercial_provisional && <span className="ml-1 align-top text-[10px] font-medium text-amber-600" title="Provisional estimate — frozen on completion">est</span>}</span>
        : "—" }] : []),
    { key: "created_at", label: "Created", hideMobile: true, render: (r) => (r.created_at || "").slice(0, 10) },
  ];

  const filters = [
    { key: "status", label: "Status", options: [{ value: "DRAFT", label: "Draft" }, { value: "COMPLETED", label: "Completed" }] },
    { key: "form_type", label: "Type", options: ["NEW_METER", "OLD_REPLACED", "REWORK", "SVR"].map((t) => ({ value: t, label: formTypeLabel(t) })) },
    { key: "final_remark_id", label: "Final Remark", options: [{ value: "__none__", label: "— No Final Remark —" }, ...frOpts] },
  ];

  return (
    <div>
      <PageHeader title="Data Entry Register" subtitle="All draft & completed submissions" />
      <ProjectScopeBanner />
      {stats && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="de-stats">
          <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 text-white shadow-sm" data-testid="de-stats-total">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-300">Total Entries</div>
            <div className="mt-1 text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm" data-testid="de-stats-draft">
            <div className="text-xs font-medium uppercase tracking-wide text-amber-600">Drafts</div>
            <div className="mt-1 text-2xl font-bold text-amber-700">{stats.draft}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm" data-testid="de-stats-completed">
            <div className="text-xs font-medium uppercase tracking-wide text-emerald-600">Completed</div>
            <div className="mt-1 text-2xl font-bold text-emerald-700">{stats.completed}</div>
          </div>
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 shadow-sm" data-testid="de-stats-last-install">
            <div className="text-xs font-medium uppercase tracking-wide text-sky-600">Last Installation Date</div>
            <div className="mt-1 text-2xl font-bold text-sky-800">{stats.last_installation_date || "—"}</div>
          </div>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm" data-testid="de-stats-last-entry">
            <div className="text-xs font-medium uppercase tracking-wide text-indigo-600">Last Data Entry Date</div>
            <div className="mt-1 text-2xl font-bold text-indigo-800">{stats.last_entry_date || "—"}</div>
          </div>
        </div>
      )}
      {drillFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800" data-testid="de-drill-banner">
          <span className="font-semibold">Filtered from Dashboard:</span>
          {drillFilters.label && <Badge variant="outline" className="border-indigo-300">{drillFilters.label}</Badge>}
          {(drillFilters.install_from || drillFilters.install_to) && <Badge variant="outline" className="border-indigo-300">Install {drillFilters.install_from} → {drillFilters.install_to}</Badge>}
          {drillFilters.status && <Badge variant="outline" className="border-indigo-300">{drillFilters.status}</Badge>}
          <Button data-testid="de-drill-clear" variant="ghost" size="sm" className="ml-auto h-6 px-2 text-indigo-700 hover:text-indigo-900" onClick={() => setDrillFilters(null)}><X className="mr-1 h-3 w-3" /> Clear</Button>
        </div>
      )}
      <Tabs defaultValue="register">
        <TabsList>
          <TabsTrigger value="register" data-testid="de-tab-register">Register</TabsTrigger>
          <TabsTrigger value="summary" data-testid="de-tab-summary">Date-wise Summary</TabsTrigger>
        </TabsList>
        <TabsContent value="register" className="mt-4">
      <DataTable testid="de-register" columns={columns} load={load} filters={filters} refreshKey={`${pid || "all"}-${drillFilters ? JSON.stringify(drillFilters) : "none"}-${refreshTick}`} searchPlaceholder="Search entry no. / consumer / material no..."
        toolbar={(canComplete || canDelete) && (
          <>
            {canComplete && (
              <Button data-testid="de-complete-ready" size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={bulkBusy} onClick={completeReady}>
                <CheckCircle2 className="mr-1 h-4 w-4" />{bulkBusy ? `Completing… ${bulkProgress}` : "Complete All Ready"}
              </Button>
            )}
            {canDelete && (
              <Button data-testid="de-clean-blank-drafts" size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" disabled={cleanBusy} onClick={cleanBlankDrafts}>
                <Eraser className="mr-1 h-4 w-4" />{cleanBusy ? "Checking…" : "Clean Blank Drafts"}
              </Button>
            )}
          </>
        )}
        rowActions={(row) => (
          <div className="flex items-center gap-1">
            <Button data-testid={`de-view-${row.id}`} variant="ghost" size="sm" onClick={() => openDetail(row)}><Eye className="h-4 w-4" /></Button>
            {row.status === "DRAFT" && canEdit && (
              <Button data-testid={`de-resume-${row.id}`} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" title="Resume Draft" onClick={() => resumeDraft(row.id)}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {row.status === "DRAFT" && canDelete && (
              <Button data-testid={`de-delete-${row.id}`} variant="ghost" size="sm" className="text-red-600 hover:text-red-700" title="Delete Draft" onClick={() => delDraft(row.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {row.status === "DRAFT" && canComplete && row.can_complete && (
              <Button data-testid={`de-complete-${row.id}`} variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700" title="Mark as Completed" disabled={completing} onClick={() => completeFromDraft(row.id)}>
                <CheckCircle2 className="h-4 w-4" />
              </Button>
            )}
            {row.status === "COMPLETED" && canReopen && (
              <Button data-testid={`de-reopen-${row.id}`} variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" title="Reopen to Edit" onClick={() => reopen(row.id)}>
                <Unlock className="h-4 w-4" />
              </Button>
            )}
          </div>
        )} />
        </TabsContent>

        <TabsContent value="summary" className="mt-4" data-testid="de-summary-tab">
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <div className="flex overflow-hidden rounded-md border border-slate-300" data-testid="de-sum-mode">
              <button type="button" data-testid="de-sum-mode-range" onClick={() => setSumSingle(false)}
                className={`h-8 px-3 text-xs font-medium transition-colors ${!sumSingle ? "bg-teal-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Date Range</button>
              <button type="button" data-testid="de-sum-mode-single" onClick={() => setSumSingle(true)}
                className={`h-8 px-3 text-xs font-medium transition-colors ${sumSingle ? "bg-teal-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Single Date</button>
            </div>
            {sumSingle ? (
              <div><Label className="text-xs text-slate-500">Date</Label><input type="date" data-testid="de-sum-day" value={sumDay} onChange={(e) => setSumDay(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            ) : (<>
              <div><Label className="text-xs text-slate-500">From</Label><input type="date" data-testid="de-sum-from" value={sumFrom} onChange={(e) => setSumFrom(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
              <div><Label className="text-xs text-slate-500">To</Label><input type="date" data-testid="de-sum-to" value={sumTo} onChange={(e) => setSumTo(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            </>)}
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase text-slate-500">Date</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">No of Activity as per DPR</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">No. of Installations (As per Data Entry)</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase text-slate-500">No. of Data Entries</th>
                </tr>
              </thead>
              <tbody>
                {!sumRows ? <tr><td colSpan={4} className="py-8 text-center text-slate-400">Loading…</td></tr>
                  : sumRows.length === 0 ? <tr><td colSpan={4} className="py-8 text-center text-slate-400" data-testid="de-sum-empty">No entries in this period</td></tr>
                  : sumRows.map((r) => (
                    <tr key={r.date} className="border-b border-slate-100 last:border-0" data-testid={`de-sum-row-${r.date}`}>
                      <td className="px-4 py-2 font-medium text-slate-700">{r.date}</td>
                      <td className="px-4 py-2 text-right"><Badge className="bg-teal-100 text-teal-700">{r.dpr_activity ?? 0}</Badge></td>
                      <td className="px-4 py-2 text-right"><Badge className="bg-sky-100 text-sky-700">{r.installations}</Badge></td>
                      <td className="px-4 py-2 text-right"><Badge className="bg-indigo-100 text-indigo-700">{r.entries}</Badge></td>
                    </tr>
                  ))}
                {sumRows && sumRows.length > 0 && (
                  <tr className="bg-slate-50 font-semibold" data-testid="de-sum-total">
                    <td className="px-4 py-2 text-slate-700">Total</td>
                    <td className="px-4 py-2 text-right text-teal-800">{sumRows.reduce((s, r) => s + (r.dpr_activity || 0), 0)}</td>
                    <td className="px-4 py-2 text-right text-sky-800">{sumRows.reduce((s, r) => s + r.installations, 0)}</td>
                    <td className="px-4 py-2 text-right text-indigo-800">{sumRows.reduce((s, r) => s + r.entries, 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}><DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-mono">{detail?.entry_number}</DialogTitle></DialogHeader>
        {detail && (() => {
          const cf = detail.render?.custom_fields || [];
          const optLabel = (c, id) => (c.options || []).find((o) => o.option_id === id)?.label || id;
          const cfValue = (c) => {
            const v = detail.values?.[c.field_id];
            if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return "—";
            if (c.type === "multiselect" && Array.isArray(v)) return v.map((x) => optLabel(c, x)).join(", ");
            if (c.type === "dropdown") return optLabel(c, v);
            return String(v);
          };
          const cs = detail.consumer_snapshot || {};
          const om = detail.old_meter || {};
          const members = detail.members_resolved || {};
          const hasMembers = Object.values(members).some((arr) => (arr || []).length);
          const hasOldMeter = om.old_meter_no || om.final_reading;
          return (
          <div className="space-y-4 text-sm" data-testid="de-detail">
            <div className="flex flex-wrap gap-2">
              <Badge className={detail.status === "COMPLETED" ? "bg-emerald-600" : "bg-amber-500"}>{detail.status}</Badge>
              <Badge variant="outline">{detail.render?.form_name || detail.form_name}</Badge>
              <Badge variant="outline">{formTypeLabel(detail.form_type)}</Badge>
              <Badge variant="outline">v{detail.form_version_no}</Badge>
              {detail.installation_date && <Badge variant="outline">Install: {detail.installation_date}</Badge>}
              {detail.created_at && <Badge variant="outline">Created: {(detail.created_at || "").slice(0, 10)}</Badge>}
            </div>

            {cs.consumer_number && (
              <div className="rounded-lg border p-3" data-testid="de-detail-consumer">
                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Consumer</div>
                <div className="font-mono font-semibold">{cs.consumer_number}</div>
                <div className="mt-1 space-y-0.5">
                  <Row label="Name" value={cs.name} />
                  {cs.mobile && <Row label="Mobile" value={cs.mobile} />}
                  {cs.address && <Row label="Address" value={cs.address} />}
                  {cs.area && <Row label="Subdivision" value={cs.area} />}
                  {cs.division && <Row label="Division" value={cs.division} />}
                  {cs.section && <Row label="Section" value={cs.section} />}
                  {cs.meter_kw && <Row label="Meter KW" value={cs.meter_kw} />}
                  {cs.discom && <Row label="Discom" value={cs.discom} />}
                  {cs.old_meter_no && <Row label="Old Meter No" value={cs.old_meter_no} />}
                </div>
              </div>
            )}

            {(detail.final_remark_text || detail.commercial_snapshot?.final_remark_text) && (
              <div className="rounded-lg border p-3" data-testid="de-detail-final-remark">
                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Final Remark</div>
                <div className="text-slate-700">{detail.final_remark_text || detail.commercial_snapshot?.final_remark_text}</div>
              </div>
            )}

            <div className="rounded-lg border p-3">
              <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Materials ({(detail.material_refs || []).length})</div>
              {(detail.material_refs || []).map((m, i) => (
                <div key={i} className="border-b py-1.5 last:border-0" data-testid={`de-detail-mat-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{m.serial}</span>
                    <span className="flex items-center gap-2 text-xs">
                      {m.type_label && <span className="text-slate-400">{m.type_label}</span>}
                      <span className="text-slate-500">{m.role.replace(/_/g, " ")}</span>
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-xs">
                    {m.current_status
                      ? <span className={`font-bold ${materialStatusColor(m.current_status)}`} data-testid={`de-detail-mat-status-${i}`}>{materialStatusLabel(m.current_status)}</span>
                      : <span className="text-slate-400">status unknown</span>}
                    {m.owner && m.owner !== "—" && <span className="text-slate-500">{m.owner}</span>}
                  </div>
                </div>
              ))}
              {(detail.material_refs || []).length === 0 && <div className="text-slate-400">None</div>}
            </div>

            {detail.status === "DRAFT" && canComplete && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="de-detail-complete-box">
                {detail.can_complete_from_saved ? (
                  <>
                    <span className="text-xs text-emerald-700">All materials are Acknowledged/Installed — this entry can be completed.</span>
                    <Button className="ml-auto bg-emerald-600 hover:bg-emerald-700" size="sm" disabled={completing} onClick={() => completeFromDraft(detail.id)} data-testid="de-detail-complete">
                      {completing ? "Completing…" : "Mark as Completed"}
                    </Button>
                  </>
                ) : (
                  <div className="text-xs text-amber-600" data-testid="de-detail-pending">
                    <div>This draft can be completed once every material reaches Acknowledged/Installed status.</div>
                    {(detail.pending_materials?.length > 0) && (
                      <div className="mt-1 font-medium">Awaiting: {detail.pending_materials.map((m) => `${m.serial || "?"}${m.type_label ? ` (${m.type_label})` : ""} — ${materialStatusLabel(m.status)}`).join("; ")}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {hasOldMeter && (
              <div className="rounded-lg border p-3" data-testid="de-detail-oldmeter">
                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Old Meter</div>
                {om.old_meter_no && <Row label="Old Meter No" value={om.old_meter_no} />}
                {om.final_reading && <Row label="Final Reading" value={om.final_reading} />}
              </div>
            )}

            {hasMembers && (
              <div className="rounded-lg border p-3" data-testid="de-detail-members">
                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Members</div>
                {Object.entries(members).map(([role, arr]) => (arr || []).length > 0 && (
                  <div key={role} className="flex justify-between gap-3 border-b py-1 last:border-0">
                    <span className="text-xs uppercase text-slate-400">{role.replace(/_/g, " ")}</span>
                    <span className="text-right font-medium text-slate-700">{arr.map((m) => `${m.name}${m.code ? ` (${m.code})` : ""}`).join(", ")}</span>
                  </div>
                ))}
              </div>
            )}

            {cf.length > 0 && (
              <div className="rounded-lg border p-3" data-testid="de-detail-custom">
                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Additional Details</div>
                {cf.map((c) => <Row key={c.field_id} label={c.label} value={cfValue(c)} />)}
              </div>
            )}

            {detail.commercial_snapshot && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="de-detail-commercial">
                <div className="mb-1 text-xs font-semibold uppercase text-emerald-700">Commercial (frozen @ {detail.commercial_snapshot.on_date})</div>
                <div className="text-slate-700">{detail.commercial_snapshot.final_remark_text}</div>
                <div className="mt-1 flex justify-between font-semibold"><span>Total</span><span>₹{detail.commercial_snapshot.total}</span></div>
              </div>
            )}

            <div className="rounded-lg border p-3" data-testid="de-detail-attachments">
              <div className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-slate-400"><Paperclip className="h-3.5 w-3.5" /> Attachments ({(detail.attachments || []).length})</div>
              <div className="space-y-2">
                {(detail.attachments || []).map((a) => <AttachmentItem key={a.id} att={a} />)}
                {(detail.attachments || []).length === 0 && <div className="text-slate-400">No attachments</div>}
              </div>
            </div>
          </div>
          );
        })()}
      </DialogContent></Dialog>

      <Dialog open={!!bulkResult} onOpenChange={(o) => !o && setBulkResult(null)}>
        <DialogContent className="max-w-md" data-testid="de-bulk-result">
          <DialogHeader><DialogTitle>Bulk Completion Result</DialogTitle></DialogHeader>
          {bulkResult && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">Completed <b className="text-emerald-700">{bulkResult.completed.length}</b> of {bulkResult.ready} ready draft(s).{bulkResult.skipped_drafts ? ` ${bulkResult.skipped_drafts} draft(s) are not ready yet.` : ""}</p>
              {bulkResult.failed.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase text-red-500">Failed ({bulkResult.failed.length})</div>
                  {bulkResult.failed.map((f) => (
                    <div key={f.entry_number} className="rounded-md border border-red-100 bg-red-50/50 p-2" data-testid={`de-bulk-fail-${f.entry_number}`}>
                      <div className="font-mono text-xs font-semibold text-slate-800">{f.entry_number}</div>
                      {(f.consumer_number || f.meter_serial) && (
                        <div className="font-mono text-[11px] text-slate-500">{f.consumer_number || "—"} · Meter {f.meter_serial || "—"}</div>
                      )}
                      <div className="text-xs text-red-600">{f.reason}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2">
                {bulkResult.failed.length > 0 && bulkResult.job_id && (
                  <Button size="sm" variant="outline" data-testid="de-bulk-result-pdf" onClick={downloadFailedPdf}><Download className="mr-1 h-4 w-4" /> Download PDF</Button>
                )}
                <Button size="sm" data-testid="de-bulk-result-close" onClick={() => setBulkResult(null)}>Close</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o && !deleting) { setDelTarget(null); setDelStep(1); } }}>
        <DialogContent className="max-w-md" data-testid="de-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-red-600">
              {delStep === 1 ? "Delete this draft entry?" : "Final confirmation"}
            </DialogTitle>
          </DialogHeader>
          {delStep === 1 ? (
            <div className="space-y-4" data-testid="de-delete-step-1">
              <p className="text-sm text-slate-600">Are you sure you want to delete this draft data entry? This is a destructive action.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" data-testid="de-delete-cancel-1" onClick={() => { setDelTarget(null); setDelStep(1); }}>Cancel</Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700" data-testid="de-delete-next" onClick={() => setDelStep(2)}>Yes, continue</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="de-delete-step-2">
              <p className="text-sm text-slate-600">This cannot be undone. Any reserved materials will be released back to stock. Please confirm to permanently delete this draft.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" data-testid="de-delete-back" disabled={deleting} onClick={() => setDelStep(1)}>Back</Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700" data-testid="de-delete-confirm" disabled={deleting} onClick={confirmDelete}>
                  {deleting ? "Deleting…" : "Confirm delete"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
